import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { deleteCompany } from '../services/companyService';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

const SuperAdminCompanies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const companiesPerPage = 10;
  const [filters, setFilters] = useState({
    status: 'all',
    industry: 'all',
    search: ''
  });
  const [stats, setStats] = useState({
    totalCompanies: 0,
    verifiedCompanies: 0,
    pendingCompanies: 0,
    totalJobs: 0
  });
  const [deletingCompany, setDeletingCompany] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterCompanies();
    setCurrentPage(1); // Reset to first page when filters change
  }, [companies, filters]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const jobsSnapshot = await getDocs(collection(db, 'jobs'));

      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Count jobs per company - use userId as companyId
      const jobCounts = {};
      jobsSnapshot.docs.forEach(jobDoc => {
        const job = jobDoc.data();
        // Jobs are linked to companies via userId field
        const companyKey = job.companyId || job.userId;
        if (companyKey) {
          jobCounts[companyKey] = (jobCounts[companyKey] || 0) + 1;
        }
      });

      console.log('Job counts by company:', jobCounts);

      // Add job counts to companies - check both company.id and company.userId
      const companiesWithJobCounts = companiesData.map(company => {
        const count = jobCounts[company.id] || jobCounts[company.userId] || 0;
        console.log(`Company ${company.companyName || company.name}: ${count} jobs`);
        return {
          ...company,
          jobCount: count
        };
      });

      setCompanies(companiesWithJobCounts);

      // Calculate stats
      const totalCompanies = companiesWithJobCounts.length;
      const verifiedCompanies = companiesWithJobCounts.filter(company => company.isVerified).length;
      const pendingCompanies = companiesWithJobCounts.filter(company => !company.isVerified).length;
      const totalJobs = jobsSnapshot.size;

      setStats({
        totalCompanies,
        verifiedCompanies,
        pendingCompanies,
        totalJobs
      });

    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCompanies = () => {
    let filtered = [...companies];

    if (filters.status !== 'all') {
      if (filters.status === 'verified') {
        filtered = filtered.filter(company => company.isVerified);
      } else if (filters.status === 'pending') {
        filtered = filtered.filter(company => !company.isVerified);
      }
    }

    if (filters.industry !== 'all') {
      filtered = filtered.filter(company => company.industry === filters.industry);
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(company => {
        const locationText = `${company.city || ''} ${company.state || ''} ${company.country || ''} ${company.location || ''}`.toLowerCase();

        return company.name?.toLowerCase().includes(searchTerm) ||
          company.companyName?.toLowerCase().includes(searchTerm) ||
          company.industry?.toLowerCase().includes(searchTerm) ||
          locationText.includes(searchTerm);
      });
    }

    setFilteredCompanies(filtered);
  };

  const handleCompanyClick = (company) => {
    setSelectedCompany(company);
    setShowCompanyModal(true);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const getUniqueIndustries = () => {
    const industries = companies.map(company => company.industry).filter(Boolean);
    return [...new Set(industries)];
  };

  // Calculate pagination
  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${companyName}?\n\nThis will permanently delete:\n- Company profile from database\n- All company data\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeletingCompany(companyId);

    try {
      const result = await deleteCompany(companyId);

      if (result.success) {
        // Remove company from local state
        setCompanies(prev => prev.filter(c => c.id !== companyId));
        setFilteredCompanies(prev => prev.filter(c => c.id !== companyId));

        // Close modal if this company was selected
        if (selectedCompany?.id === companyId) {
          setShowCompanyModal(false);
          setSelectedCompany(null);
        }

        // Update stats
        setStats(prev => ({
          ...prev,
          totalCompanies: prev.totalCompanies - 1
        }));

        alert('Company deleted successfully!');
      } else {
        alert(`Failed to delete company: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('An error occurred while deleting the company. Please try again.');
    } finally {
      setDeletingCompany(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-primary-900 via-primary-800 to-slate-900 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-20 translate-x-20 pointer-events-none"></div>
        <div className="relative z-10 space-y-2">
          <h1 className="text-4xl font-black tracking-tight">Corporate Partner Registry</h1>
          <p className="text-slate-300 text-base max-w-2xl">Authenticate, audit, and regulate corporate partner accounts.</p>
        </div>
      </div>

      {/* Redesigned Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border-2 border-primary-100 shadow-md flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-slate-400 text-xs font-extrabold uppercase tracking-wide">Registered Corporates</p>
            <p className="text-3xl font-black text-slate-800">{stats.totalCompanies}</p>
          </div>
          <div className="w-12 h-12 bg-primary-50 text-primary-600 rounded-2xl flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border-2 border-secondary-100 shadow-md flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-slate-400 text-xs font-extrabold uppercase tracking-wide">System-wide Listings</p>
            <p className="text-3xl font-black text-slate-800">{stats.totalJobs}</p>
          </div>
          <div className="w-12 h-12 bg-secondary-50 text-secondary-600 rounded-2xl flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
            </svg>
          </div>
        </div>
      </div>

      {/* Redesigned Query Filters */}
      <div className="bg-white rounded-3xl border-2 border-slate-100 p-6 shadow-md">
        <h2 className="text-xs font-extrabold text-slate-400 uppercase tracking-widest mb-4">Search & Query Parameters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-2">Query Partner Names</label>
            <input
              type="text"
              placeholder="Filter by corporate title, sector, or region..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-slate-50/50"
            />
          </div>

          <div>
            <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-2">Market Sector</label>
            <select
              value={filters.industry}
              onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-slate-50/50"
            >
              <option value="all">All Market Sectors</option>
              {getUniqueIndustries().map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: 'all', industry: 'all', search: '' })}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-xl transition-all duration-200"
            >
              Reset Query Filters
            </button>
          </div>
        </div>
      </div>

      {/* Corporate Grid Directory */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-800">
            Verified & Pending Partners ({filteredCompanies.length})
          </h2>
        </div>

        {filteredCompanies.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentCompanies.map((company) => (
                <div
                  key={company.id}
                  onClick={() => handleCompanyClick(company)}
                  className="bg-white rounded-3xl border-2 border-slate-100 p-6 cursor-pointer hover:shadow-2xl hover:border-primary-450 hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between h-96 relative overflow-hidden group shadow-md animate-fade-in-up"
                >
                  {/* Decorative top corner gradient */}
                  <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-primary-600 to-secondary-500"></div>
                  
                  <div className="space-y-4">
                    {/* Header: Initial & Title */}
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-primary-600 to-secondary-500 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-md flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                        {(company.companyName || company.name)?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-black text-slate-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {company.companyName || company.name}
                        </h3>
                        <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wide">
                          {company.industry || 'Market Sector Unspecified'}
                        </p>
                      </div>
                    </div>

                    {/* Metadata tags */}
                    <div className="space-y-2.5">
                      <div className="flex items-center text-xs font-semibold text-slate-500">
                        <span className="mr-2">📍</span>
                        <span className="truncate">
                          {company.city || company.state || company.country
                            ? `${company.city || ''}, ${company.state || ''}, ${company.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                            : company.location || 'Location Not Specified'
                          }
                        </span>
                      </div>
                      <div className="flex items-center text-xs font-semibold text-slate-500">
                        <span className="mr-2">👥</span>
                        <span>Scale: {company.companySize || 'Size Unspecified'}</span>
                      </div>
                      <div className="flex items-center text-xs font-semibold text-slate-500">
                        <span className="mr-2">💼</span>
                        <span className="font-extrabold text-secondary-600">{company.jobCount} Active Listings</span>
                      </div>
                    </div>

                    {/* Description excerpt */}
                    <p className="text-slate-500 text-xs line-clamp-3 leading-relaxed">
                      {company.description || 'No description available for this corporate partner.'}
                    </p>
                  </div>

                  {/* Actions & Reg Date */}
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-semibold mt-auto">
                    <span className="text-slate-400">Since: {formatDate(company.createdAt)}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCompany(company.id, company.companyName || company.name);
                        }}
                        disabled={deletingCompany === company.id}
                        className={`p-2 rounded-xl transition-all duration-200 flex items-center justify-center ${deletingCompany === company.id
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-red-50 hover:bg-red-100 text-red-650 border border-red-100 shadow-sm'
                          }`}
                        title="Decommission Account"
                      >
                        {deletingCompany === company.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                      
                      {company.jobCount > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/superadmin/jobs', {
                              state: {
                                companyFilter: company.userId || company.id,
                                companyName: company.companyName || company.name
                              }
                            });
                          }}
                          className="px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-xl transition-colors border border-primary-100 flex items-center gap-1 font-bold text-[11px]"
                        >
                          Inspect Postings
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="p-6 border-t border-slate-100">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Presenting <span className="text-slate-800 font-extrabold">{indexOfFirstCompany + 1}</span> -{' '}
                    <span className="text-slate-800 font-extrabold">{Math.min(indexOfLastCompany, filteredCompanies.length)}</span> of{' '}
                    <span className="text-slate-800 font-extrabold">{filteredCompanies.length}</span> registered corporates
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors ${currentPage === 1
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                        : 'bg-primary-600 text-white hover:bg-primary-750'
                        }`}
                    >
                      Previous Page
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => handlePageChange(pageNumber)}
                              className={`w-9 h-9 rounded-xl font-bold text-xs transition-colors ${currentPage === pageNumber
                                ? 'bg-primary-600 text-white shadow-md'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        } else if (
                          pageNumber === currentPage - 2 ||
                          pageNumber === currentPage + 2
                        ) {
                          return (
                            <span key={pageNumber} className="px-1 text-slate-400 font-extrabold text-xs">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>

                    {/* Next Button */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors ${currentPage === totalPages
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                        : 'bg-primary-600 text-white hover:bg-primary-750'
                        }`}
                    >
                      Next Page
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
            <p className="text-gray-500">No companies match your current filters.</p>
          </div>
        )}
      </div>

      {/* Company Detail Modal */}
      {showCompanyModal && selectedCompany && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[2.5rem] max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-slate-100 flex flex-col">
            
            {/* Header with gradient background */}
            <div className="relative bg-gradient-to-r from-[#0e3a61] via-[#09253f] to-[#051728] p-8 text-white flex-shrink-0">
              <div className="absolute inset-0 bg-[radial-gradient(#e2822f_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none"></div>
              <div className="absolute -bottom-10 right-10 w-48 h-48 bg-[#508d0c]/10 rounded-full blur-3xl pointer-events-none"></div>
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-6">
                  {/* Company Logo or Initial */}
                  <div className="relative">
                    {selectedCompany.logoUrl ? (
                      <img
                        src={selectedCompany.logoUrl}
                        alt={selectedCompany.companyName || selectedCompany.name}
                        className="w-20 h-20 rounded-2xl object-cover border-4 border-white/20 shadow-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-black text-3xl border-4 border-white/20 shadow-lg">
                        {(selectedCompany.companyName || selectedCompany.name)?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                    )}
                    {/* Verification badge */}
                    <div className="absolute -bottom-2 -right-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-3 border-white shadow-lg ${selectedCompany.isVerified
                        ? 'bg-[#508d0c]'
                        : 'bg-[#e2822f]'
                        }`}>
                        {selectedCompany.isVerified ? (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-2xl sm:text-3xl font-black mb-1.5">{selectedCompany.companyName || selectedCompany.name}</h2>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="px-3.5 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-xs font-bold border border-white/15">
                        {selectedCompany.industry || 'Technology'}
                      </span>
                      {selectedCompany.isVerified ? (
                        <span className="px-3 py-1 bg-[#508d0c]/20 text-[#a3e635] rounded-full text-[10px] font-extrabold uppercase tracking-wider border border-[#508d0c]/30">
                          Verified Partner
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-[#e2822f]/20 text-[#fdba74] rounded-full text-[10px] font-extrabold uppercase tracking-wider border border-[#e2822f]/30">
                          Awaiting Audit
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowCompanyModal(false)}
                  className="p-2.5 hover:bg-white/10 rounded-xl transition-colors text-white/80 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content Area - Scrollable */}
            <div className="p-8 bg-slate-50/50 overflow-y-auto flex-1">
              {/* Asymmetrical Split Grid Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* Left Column: Detailed Content (7 cols) */}
                <div className="lg:col-span-7 space-y-6">
                  {/* Corporate Biography */}
                  <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#0e3a61]/5 rounded-xl flex items-center justify-center text-[#0e3a61]">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-black text-slate-800">Corporate Biography</h3>
                    </div>
                    <div className="prose max-w-none text-slate-600 text-sm leading-relaxed">
                      {typeof selectedCompany.description === 'string'
                        ? selectedCompany.description.split('\n').map((paragraph, index) => (
                          <p key={index} className="mb-4">{paragraph}</p>
                        ))
                        : <p className="mb-4">{selectedCompany.description || 'No description available for this corporate partner.'}</p>
                      }
                    </div>
                  </div>

                  {/* Employee Incentives */}
                  {selectedCompany.benefits && selectedCompany.benefits.length > 0 && selectedCompany.benefits.some(b => b.trim()) && (
                    <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-[#508d0c]/5 rounded-xl flex items-center justify-center text-[#508d0c]">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-black text-slate-800">Employee Incentives</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Array.isArray(selectedCompany.benefits)
                          ? selectedCompany.benefits.filter(b => b && b.trim()).map((benefit, index) => (
                            <div key={index} className="flex items-center gap-3 p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                              <div className="w-2 h-2 bg-[#508d0c] rounded-full flex-shrink-0"></div>
                              <span className="text-slate-700 text-xs font-semibold">{benefit}</span>
                            </div>
                          ))
                          : typeof selectedCompany.benefits === 'string'
                            ? selectedCompany.benefits.split('\n').map((benefit, index) => (
                              <div key={index} className="flex items-center gap-3 p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                                <div className="w-2 h-2 bg-[#508d0c] rounded-full flex-shrink-0"></div>
                                <span className="text-slate-700 text-xs font-semibold">{benefit}</span>
                              </div>
                            ))
                            : <div className="flex items-center gap-3 p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                              <div className="w-2 h-2 bg-[#508d0c] rounded-full flex-shrink-0"></div>
                              <span className="text-slate-700 text-xs font-semibold">{selectedCompany.benefits}</span>
                            </div>
                        }
                      </div>
                    </div>
                  )}

                  {/* Strategic Objective, Future Trajectory, Corporate Ethics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {selectedCompany.mission && selectedCompany.mission.trim() && (
                      <div className="bg-white rounded-[2rem] p-5 shadow-sm border border-slate-100 flex flex-col">
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-8 h-8 bg-[#e2822f]/5 rounded-lg flex items-center justify-center text-[#e2822f]">
                            <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <h4 className="text-sm font-black text-slate-800">Strategic Objective</h4>
                        </div>
                        <p className="text-slate-500 text-xs leading-relaxed flex-1">{selectedCompany.mission}</p>
                      </div>
                    )}

                    {selectedCompany.vision && selectedCompany.vision.trim() && (
                      <div className="bg-white rounded-[2rem] p-5 shadow-sm border border-slate-100 flex flex-col">
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                            <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                          <h4 className="text-sm font-black text-slate-800">Future Trajectory</h4>
                        </div>
                        <p className="text-slate-500 text-xs leading-relaxed flex-1">{selectedCompany.vision}</p>
                      </div>
                    )}

                    {selectedCompany.culture && selectedCompany.culture.trim() && (
                      <div className="bg-white rounded-[2rem] p-5 shadow-sm border border-slate-100 flex flex-col">
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-8 h-8 bg-[#508d0c]/5 rounded-lg flex items-center justify-center text-[#508d0c]">
                            <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <h4 className="text-sm font-black text-slate-800">Corporate Ethics</h4>
                        </div>
                        <p className="text-slate-500 text-xs leading-relaxed flex-1">{selectedCompany.culture}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column: Institutional Profile Details (5 cols) */}
                <div className="lg:col-span-5 space-y-6">
                  <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100 space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                      <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-black text-slate-800">Institutional Profile</h3>
                    </div>

                    <div className="space-y-4 text-xs font-semibold">
                      {/* Market Sector */}
                      <div className="flex items-center justify-between p-3.5 bg-slate-50/70 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-base">🏢</span>
                          <span className="text-slate-500">Market Sector</span>
                        </div>
                        <span className="text-slate-800 font-bold">{selectedCompany.industry || 'Unspecified'}</span>
                      </div>

                      {/* HQ Location */}
                      <div className="flex items-center justify-between p-3.5 bg-slate-50/70 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-base">📍</span>
                          <span className="text-slate-500">HQ Location</span>
                        </div>
                        <span className="text-slate-800 font-bold max-w-[200px] text-right truncate">
                          {selectedCompany.city || selectedCompany.state || selectedCompany.country
                            ? `${selectedCompany.city || ''}, ${selectedCompany.state || ''}, ${selectedCompany.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                            : selectedCompany.location || 'Unspecified'
                          }
                        </span>
                      </div>

                      {/* Workforce Scale */}
                      <div className="flex items-center justify-between p-3.5 bg-slate-50/70 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-base">👥</span>
                          <span className="text-slate-500">Workforce Scale</span>
                        </div>
                        <span className="text-slate-800 font-bold">{selectedCompany.companySize || 'Unspecified'}</span>
                      </div>

                      {/* Legal Structure */}
                      <div className="flex items-center justify-between p-3.5 bg-slate-50/70 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-base">⚖️</span>
                          <span className="text-slate-500">Legal Structure</span>
                        </div>
                        <span className="text-slate-800 font-bold">{selectedCompany.companyType || 'Unspecified'}</span>
                      </div>

                      {/* Inception Year */}
                      <div className="flex items-center justify-between p-3.5 bg-slate-50/70 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-base">📅</span>
                          <span className="text-slate-500">Inception Year</span>
                        </div>
                        <span className="text-slate-800 font-bold">{selectedCompany.foundedYear || 'Unspecified'}</span>
                      </div>

                      {/* Active Listings count banner */}
                      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-[#0e3a61]/5 rounded-2xl border border-blue-100">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">💼</span>
                          <span className="text-blue-900 font-bold">Active Listings</span>
                        </div>
                        <span className="text-2xl font-black text-[#0e3a61]">{selectedCompany.jobCount || 0}</span>
                      </div>

                      {/* Data Integrity Score (Profile Completion) */}
                      <div className="p-4 bg-slate-50/50 rounded-2xl border border-slate-150 space-y-2">
                        <div className="flex justify-between items-center text-[10px] font-extrabold text-slate-500 uppercase tracking-widest">
                          <span>Data Integrity Score</span>
                          <span className="text-slate-800 text-sm font-black">{selectedCompany.profileCompletion || 0}%</span>
                        </div>
                        <div className="bg-slate-200 rounded-full h-2.5 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-[#508d0c] to-[#a3e635] h-full rounded-full transition-all duration-500"
                            style={{ width: `${selectedCompany.profileCompletion || 0}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Digital Domain */}
                      {selectedCompany.website && selectedCompany.website.trim() && (
                        <a
                          href={selectedCompany.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-[#0e3a61] to-[#09253f] hover:from-[#09253f] hover:to-[#051728] text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all duration-200"
                        >
                          🌐 Launch Digital Domain
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Recruiters contact block */}
                  {selectedCompany.recruiters && selectedCompany.recruiters.length > 0 && (
                    <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100 space-y-4">
                      <h4 className="text-xs font-extrabold text-slate-400 uppercase tracking-widest">Authorized Agents ({selectedCompany.recruiters.length})</h4>
                      <div className="space-y-3">
                        {selectedCompany.recruiters.map((recruiter, index) => (
                          <div key={index} className="bg-slate-50/50 p-4 rounded-2xl border border-slate-150 flex flex-col gap-1 text-xs">
                            <div className="flex items-center gap-2.5 mb-1.5">
                              <div className="w-7 h-7 bg-[#508d0c]/10 text-[#508d0c] rounded-lg flex items-center justify-center font-bold text-xs">
                                {recruiter.name?.charAt(0)?.toUpperCase() || 'A'}
                              </div>
                              <p className="font-extrabold text-slate-800 text-sm">{recruiter.name}</p>
                            </div>
                            <div className="text-slate-500 font-semibold space-y-1 pl-9">
                              <p className="break-all">📧 {recruiter.email}</p>
                              {recruiter.phone && <p>📞 {recruiter.phone}</p>}
                              {recruiter.position && (
                                <p className="mt-1.5">
                                  <span className="text-[10px] uppercase font-bold text-[#0e3a61] bg-[#0e3a61]/5 px-2 py-0.5 rounded-md border border-[#0e3a61]/10">
                                    {recruiter.position}
                                  </span>
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Corporate Gallery */}
                  {selectedCompany.companyImages && selectedCompany.companyImages.length > 0 && (
                    <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100 space-y-4">
                      <h4 className="text-xs font-extrabold text-slate-400 uppercase tracking-widest">Gallery Assets ({selectedCompany.companyImages.length})</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedCompany.companyImages.slice(0, 4).map((image, index) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Corporate Asset ${index + 1}`}
                            className="w-full h-20 object-cover rounded-xl border border-slate-100 hover:scale-105 transition-transform duration-200"
                          />
                        ))}
                      </div>
                      {selectedCompany.companyImages.length > 4 && (
                        <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wide">
                          +{selectedCompany.companyImages.length - 4} additional assets indexed
                        </p>
                      )}
                    </div>
                  )}
                </div>

              </div>
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminCompanies;
